/** OwnersTab — primary + co-owner management via search-as-you-type comboboxes.
 *
 * Backend is search-only (``/admin/users?q=…``) so we never preload the full
 * org user list to the client. Each combobox debounces its input and fetches
 * matches on demand. Actions are one-at-a-time (set primary / add co-owner /
 * remove co-owner) so the server-side state always matches what the user
 * just saw confirmed in the UI.
 *
 * There is no shadcn combobox component; this builds a minimal
 * search-and-select dropdown on plain Input + an absolutely positioned
 * result list — not a general-purpose combobox, just enough for this form.
 */

import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useParams } from 'react-router-dom';
import { Loader2, Trash2 } from 'lucide-react';

import { Avatar, AvatarFallback } from '@/shared/ui/avatar';
import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import { Card } from '@/shared/ui/card';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import { cn } from '@/shared/lib/cn';
import {
  addCoOwner,
  listAgentOwners,
  listUsers,
  removeAgentOwner,
  setPrimaryOwner,
  type OwnerInfo,
  type UserOption,
} from '../../../services/agentHub';
import { useAuth } from '../../../contexts/AuthContext';
import { log } from '@/shared/logger';

const SEARCH_DEBOUNCE_MS = 200;

function userLabel(u: UserOption): string {
  return u.display_name && u.display_name !== u.email
    ? `${u.display_name} <${u.email}>`
    : u.email;
}

function ownerLabel(owner: OwnerInfo): string {
  if (owner.display_name && owner.email) return `${owner.display_name} <${owner.email}>`;
  return owner.email ?? owner.user_id;
}

/** Two-letter avatar fallback from a display name, email, or raw user id. */
function initials(owner: OwnerInfo): string {
  const source = owner.display_name?.trim() || owner.email || owner.user_id;
  const parts = source.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }
  return source.slice(0, 2).toUpperCase();
}

/** One owner row: avatar, name/email, role badge, and an optional action slot. */
function OwnerRow({ owner, actions }: { owner: OwnerInfo; actions?: ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-3 px-4 py-3">
      <div className="flex min-w-0 items-center gap-3">
        <Avatar>
          <AvatarFallback>{initials(owner)}</AvatarFallback>
        </Avatar>
        <div className="min-w-0">
          <p className="m-0 truncate text-sm font-medium text-foreground">
            {owner.display_name ?? owner.email ?? owner.user_id}
          </p>
          {owner.email && owner.display_name ? (
            <p className="m-0 truncate text-xs text-muted-foreground">{owner.email}</p>
          ) : null}
        </div>
      </div>
      <div className="flex shrink-0 items-center gap-2">
        <Badge variant={owner.is_primary ? 'default' : 'secondary'}>
          {owner.is_primary ? 'Primary' : 'Co-owner'}
        </Badge>
        {actions}
      </div>
    </div>
  );
}

/**
 * Debounced user search hook — returns results + loading state for a query.
 * Cancels in-flight responses that are no longer relevant when the user
 * keeps typing, so stale results never overwrite newer ones.
 */
function useUserSearch(): {
  results: UserOption[];
  loading: boolean;
  error: string | null;
  search: (q: string) => void;
} {
  const [results, setResults] = useState<UserOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const reqIdRef = useRef(0);

  const search = useCallback((q: string) => {
    if (timerRef.current) clearTimeout(timerRef.current);
    const reqId = ++reqIdRef.current;
    setResults([]);
    setError(null);
    if (q.trim().length < 2) {
      setLoading(false);
      return;
    }
    setLoading(true);
    timerRef.current = setTimeout(async () => {
      try {
        const users = await listUsers(q);
        // Drop results if the user has typed again since we started.
        if (reqId === reqIdRef.current) setResults(users);
      } catch (err) {
        log.warn('owners_tab_user_search_failed', { query: q, error: err });
        if (reqId === reqIdRef.current) setError('Unable to search users. Please try again.');
      } finally {
        if (reqId === reqIdRef.current) setLoading(false);
      }
    }, SEARCH_DEBOUNCE_MS);
  }, []);

  useEffect(() => {
    const requestId = reqIdRef;
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      ++requestId.current;
    };
  }, []);

  return { results, loading, error, search };
}

interface UserSearchComboProps {
  id: string;
  label: string;
  query: string;
  onQueryChange: (q: string) => void;
  results: UserOption[];
  loading: boolean;
  error: string | null;
  selectedKey: string | null;
  onSelect: (user: UserOption | null) => void;
  hint: string;
}

function UserSearchCombo({
  id,
  label,
  query,
  onQueryChange,
  results,
  loading,
  error,
  selectedKey,
  onSelect,
  hint,
}: UserSearchComboProps) {
  const [open, setOpen] = useState(false);

  return (
    <div className="relative flex flex-1 flex-col gap-1">
      <Label htmlFor={id}>{label}</Label>
      <Input
        id={id}
        value={query}
        onChange={(e) => {
          onQueryChange(e.target.value);
          onSelect(null);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        placeholder="Search users…"
        autoComplete="off"
      />
      {open && !selectedKey && query.trim().length >= 2 && (
        <ul className="absolute top-full z-10 mt-1 max-h-48 w-full overflow-y-auto rounded-lg border border-border bg-popover p-1 shadow-md">
          {loading ? (
            <li className="px-2 py-1.5 text-xs text-muted-foreground">Searching…</li>
          ) : error ? (
            <li role="alert" className="px-2 py-1.5 text-xs text-destructive">{error}</li>
          ) : results.length === 0 ? (
            <li role="status" className="px-2 py-1.5 text-xs text-muted-foreground">
              No matching users in this environment.
            </li>
          ) : (
            results.map((u) => (
              <li key={u.user_id}>
                <button
                  type="button"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => {
                    onSelect(u);
                    setOpen(false);
                  }}
                  className={cn(
                    'w-full rounded-md px-2 py-1.5 text-left text-sm hover:bg-accent',
                    selectedKey === u.user_id && 'bg-accent',
                  )}
                >
                  {userLabel(u)}
                </button>
              </li>
            ))
          )}
        </ul>
      )}
      <p className="text-xs text-muted-foreground">{hint}</p>
    </div>
  );
}

export default function OwnersTab() {
  const { agentId = '' } = useParams<{ agentId: string }>();
  const { capabilities } = useAuth();
  const isAdmin = capabilities?.is_admin === true;
  // ``internal_user_id`` is the DB ``users.id`` — the same UUID stored in
  // ``agent_owners.user_id``. Comparing against ``capabilities.user_id``
  // (Okta external sub) was the old bug where non-admin primary owners
  // could never edit co-owners.
  const currentInternalId = capabilities?.internal_user_id ?? null;

  const [owners, setOwners] = useState<OwnerInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [savingPrimary, setSavingPrimary] = useState(false);
  const [addingCoOwner, setAddingCoOwner] = useState(false);

  const primarySearch = useUserSearch();
  const coOwnerSearch = useUserSearch();
  const [primaryQuery, setPrimaryQuery] = useState('');
  const [primarySelection, setPrimarySelection] = useState<UserOption | null>(null);
  const [coOwnerQuery, setCoOwnerQuery] = useState('');
  const [coOwnerSelection, setCoOwnerSelection] = useState<UserOption | null>(null);

  const primary = useMemo(() => owners.find((o) => o.is_primary) ?? null, [owners]);
  const coOwners = useMemo(() => owners.filter((o) => !o.is_primary), [owners]);

  const isPrimaryCaller =
    !!currentInternalId && primary?.user_id === currentInternalId;
  const canEditCoOwners = isAdmin || isPrimaryCaller;

  const load = useCallback(async () => {
    if (!agentId) return;
    setLoading(true);
    setError(null);
    try {
      const ownerRows = await listAgentOwners(agentId);
      setOwners(ownerRows);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load owners');
    } finally {
      setLoading(false);
    }
  }, [agentId]);

  useEffect(() => { load(); }, [load]);

  const onPrimaryInputChange = (q: string) => {
    setPrimaryQuery(q);
    setPrimarySelection(null);
    primarySearch.search(q);
  };

  const onCoOwnerInputChange = (q: string) => {
    setCoOwnerQuery(q);
    setCoOwnerSelection(null);
    coOwnerSearch.search(q);
  };

  const handleSetPrimary = async () => {
    if (!primarySelection) return;
    setSavingPrimary(true);
    setError(null);
    try {
      await setPrimaryOwner(agentId, primarySelection.email);
      setPrimaryQuery('');
      setPrimarySelection(null);
      primarySearch.search('');
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to set primary owner');
    } finally {
      setSavingPrimary(false);
    }
  };

  const handleAddCoOwner = async () => {
    if (!coOwnerSelection) return;
    const target = coOwnerSelection;
    // Guard: don't let the user add the current primary or an existing co-owner.
    if (primary?.user_id === target.user_id) {
      setError('That user is already the primary owner.');
      return;
    }
    if (coOwners.some((o) => o.user_id === target.user_id)) {
      setError('That user is already a co-owner.');
      return;
    }
    setAddingCoOwner(true);
    setError(null);
    try {
      await addCoOwner(agentId, target.email);
      setCoOwnerQuery('');
      setCoOwnerSelection(null);
      coOwnerSearch.search('');
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add co-owner');
    } finally {
      setAddingCoOwner(false);
    }
  };

  const handleRemoveCoOwner = async (userId: string) => {
    setError(null);
    try {
      await removeAgentOwner(agentId, userId);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to remove co-owner');
    }
  };

  if (loading) {
    return (
      <div role="status" aria-label="Loading owners" className="flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="size-4 animate-spin" aria-hidden="true" />
        Loading owners…
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6 pt-4">
      {error && (
        <p role="alert" className="m-0 text-sm text-destructive">
          {error}
        </p>
      )}

      {/* Primary Owner — admin-only change path */}
      <div className="flex flex-col gap-2">
        <p className="text-sm font-bold text-foreground">Primary owner</p>
        <p className="text-sm text-muted-foreground">
          Exactly one per agent. Only admins can change this. To replace the
          primary, pick a new user below — the previous primary is demoted to
          co-owner automatically.
        </p>
        {primary ? (
          <Card className="gap-0 p-0">
            <ul aria-label="Current primary owner">
              <li>
                <OwnerRow owner={primary} />
              </li>
            </ul>
          </Card>
        ) : (
          <p className="text-sm text-muted-foreground">No primary owner set.</p>
        )}
        {isAdmin && (
          <div className="flex items-end gap-2">
            <UserSearchCombo
              id="owners-primary-search"
              label={primary ? 'Replace primary owner with' : 'Set primary owner'}
              query={primaryQuery}
              onQueryChange={onPrimaryInputChange}
              results={primarySearch.results}
              loading={primarySearch.loading}
              error={primarySearch.error}
              selectedKey={primarySelection?.user_id ?? null}
              onSelect={(user) => {
                setPrimarySelection(user);
                if (user) setPrimaryQuery(userLabel(user));
              }}
              hint="Type 2+ characters to search active @adobe.com users in this environment."
            />
            <Button
              onClick={handleSetPrimary}
              disabled={savingPrimary || !primarySelection}
            >
              {primary ? 'Replace' : 'Set'}
            </Button>
          </div>
        )}
      </div>

      {/* Co-owners — primary or admin can manage */}
      <div className="flex flex-col gap-2">
        <p className="text-sm font-bold text-foreground">Co-owners</p>
        <p className="text-sm text-muted-foreground">
          {canEditCoOwners
            ? 'Search for a user and add them. Remove a co-owner with the trash icon.'
            : 'Only the primary owner or an admin can change co-owners.'}
        </p>

        {coOwners.length === 0 ? (
          <p className="text-sm text-muted-foreground">No co-owners yet.</p>
        ) : (
          <Card className="gap-0 p-0">
            <ul aria-label="Co-owners" className="divide-y divide-border">
              {coOwners.map((owner) => (
                <li key={owner.id}>
                  <OwnerRow
                    owner={owner}
                    actions={
                      canEditCoOwners ? (
                        <Button
                          aria-label={`Remove ${ownerLabel(owner)}`}
                          onClick={() => handleRemoveCoOwner(owner.user_id)}
                          variant="ghost"
                          size="icon-sm"
                        >
                          <Trash2 />
                        </Button>
                      ) : undefined
                    }
                  />
                </li>
              ))}
            </ul>
          </Card>
        )}

        {canEditCoOwners && (
          <div className="flex items-end gap-2">
            <UserSearchCombo
              id="owners-co-owner-search"
              label="Add co-owner"
              query={coOwnerQuery}
              onQueryChange={onCoOwnerInputChange}
              results={coOwnerSearch.results}
              loading={coOwnerSearch.loading}
              error={coOwnerSearch.error}
              selectedKey={coOwnerSelection?.user_id ?? null}
              onSelect={(user) => {
                setCoOwnerSelection(user);
                if (user) setCoOwnerQuery(userLabel(user));
              }}
              hint="Type 2+ characters to search active @adobe.com users in this environment."
            />
            <Button
              onClick={handleAddCoOwner}
              disabled={addingCoOwner || !coOwnerSelection}
            >
              Add
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
